import type { SendPartnerNotificationDto } from "@simbelapp/order-sdk";
import { PartnerNotificationErrorEnum } from "~/utils/enums/session-notifications.enums";
import type { IPartnerNotificationInput } from "~/utils/interfaces/session-notifications-interfaces";

/** Shown whenever orders refuses the send for a reason the admin cannot fix from the form (422). */
export const PARTNER_NOTIFICATION_SEND_FAILED = "L'envoi a échoué, rien n'a été enregistré.";

const ERROR_MESSAGES: Record<PartnerNotificationErrorEnum, string> = {
  [PartnerNotificationErrorEnum.NO_RECIPIENT]:
    "Aucun destinataire : ajoutez une adresse ou renseignez le contact prestataire de la session.",
  [PartnerNotificationErrorEnum.DOCUMENTS_NOT_IN_SESSION]:
    "Certains documents joints n'appartiennent pas à cette session : retirez-les puis renvoyez.",
  [PartnerNotificationErrorEnum.TEMPLATE_NOT_FOUND]:
    "Le modèle de mail sélectionné n'existe plus : choisissez-en un autre.",
};

const INVALID_EMAIL_MESSAGE = "Une des adresses saisies n'est pas une adresse e-mail valide.";
const MISSING_FIELD_MESSAGE = "Le sujet et le contenu du mail sont obligatoires.";
const SESSION_NOT_FOUND_MESSAGE = "Cette session est introuvable : rien n'a été envoyé.";
const FORBIDDEN_MESSAGE = "Vous n'avez pas les droits pour écrire au contact prestataire.";

/** Whether an HTML body carries any text: an emptied editor still answers `<p></p>`. */
export function hasMailContent(content: string | null | undefined): boolean {
  if (!content) return false;
  return (
    content
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim().length > 0
  );
}

/** Trimmed, lowercased and deduplicated, as orders stores them. */
function normalizeAddresses(addresses: string[]): string[] {
  return [...new Set(addresses.map((address) => address.trim().toLowerCase()).filter(Boolean))];
}

/** The orders payload of a partner send; copies already listed as recipients are dropped. */
export function buildPartnerNotificationPayload(input: IPartnerNotificationInput): SendPartnerNotificationDto {
  const recipients = normalizeAddresses(input.recipients);
  const cc = normalizeAddresses(input.cc).filter((address) => !recipients.includes(address));

  return {
    recipients,
    cc,
    subject: input.subject.trim(),
    content: input.content ?? "",
    ...(input.templateId ? { templateId: input.templateId } : {}),
    documentIds: [...new Set(input.documentIds)],
    includeAttendeeList: input.includeAttendeeList,
  };
}

/**
 * French copy of a refused partner send, read from the message orders answers with. Keyed on the
 * message, never the status: the catch-all filter turns every unmapped failure into a 422, whose
 * body only says « UNPROCESSABLE_ENTITY ».
 */
export function partnerNotificationErrorMessage(message: unknown): string {
  // A class-validator 400 carries one message per failed constraint.
  if (Array.isArray(message)) {
    const constraints = message.map(String);
    if (constraints.some((constraint) => constraint.includes("must be an email"))) return INVALID_EMAIL_MESSAGE;
    if (constraints.some((constraint) => constraint.includes("should not be empty"))) return MISSING_FIELD_MESSAGE;
    return PARTNER_NOTIFICATION_SEND_FAILED;
  }

  const raw = typeof message === "string" ? message : "";
  const known = Object.values(PartnerNotificationErrorEnum).find((value) => raw.includes(value));
  if (known) return ERROR_MESSAGES[known];
  if (/^Session .+ not found$/.test(raw)) return SESSION_NOT_FOUND_MESSAGE;
  if (/forbidden|unauthorized/i.test(raw)) return FORBIDDEN_MESSAGE;

  return PARTNER_NOTIFICATION_SEND_FAILED;
}

/**
 * The message of a rejected send: a bare `message` (a string, or an array for a validation 400), or a
 * `useSDKApi` rejection, whose `response` body is still unread.
 */
export async function requestErrorMessage(error: unknown): Promise<unknown> {
  if (typeof error === "string" || Array.isArray(error)) return error;

  const response = (error as { response?: Response } | null)?.response;
  const body = await response?.json?.().catch(() => null);
  return body?.message ?? (error as { message?: unknown } | null)?.message;
}

/** French copy of a rejected partner send. */
export async function partnerNotificationRequestError(error: unknown): Promise<string> {
  return partnerNotificationErrorMessage(await requestErrorMessage(error));
}
